import { RefObject, useEffect, useRef, useState } from "react";

export interface UseIsOverflowingResult<T extends HTMLElement> {
  ref: RefObject<T | null>;
  isOverflow: boolean;
}

/**
 * React hook that tells you whether the referenced element is overflowing
 * (either in width or height). Automatically re-checks on window resize.
 */
export function useIsOverflowing<
  T extends HTMLElement = HTMLDivElement,
>(): UseIsOverflowingResult<T> {
  const ref = useRef<T>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      setIsOverflow(
        el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight,
      );
    };

    check();
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("resize", check);
    };
  }, [ref]);

  return { ref, isOverflow };
}
